// Bascule FR/EN (Phase 95, D-03).
//
// Deux contrôles à deux états, pas un menu déroulant : à deux langues un select
// coûte un clic de plus et un piège d'accessibilité de plus. Le jour où une 3ᵉ
// langue arrive, c'est CE composant qui se redécoupe — pas l'architecture.
//
// Libellé visible court (`FR` / `EN`), nom accessible complet (« Français » /
// « English ») : les deux formes se contiennent l'une l'autre à la casse près,
// donc WCAG 2.5.3 « Label in Name » est respecté.
// Refs: phase 95.
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageProvider'
import { withLang } from '../i18n/urlLang'
import type { Lang, MsgKey } from '../i18n/types'

function LangButton({ code, short, label }: { code: Lang; short: string; label: MsgKey }) {
  const { lang, setLang, t } = useLanguage()
  const { pathname } = useLocation()
  const active = code === lang
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={t(label)}
      onClick={async () => {
        // Cliquer sur la langue active ne réécrit rien et ne pousse rien vers
        // le compte : ce n'est pas un choix, c'est un clic perdu.
        if (active) return
        // D-05 — l'ORDRE est tout le sujet. `setLang` écrit le stockage puis
        // ATTEND la RPC ; la navigation qui suit tue toute requête en vol, donc
        // la lancer avant perdrait silencieusement la préférence côté compte.
        await setLang(code)
        // Rechargement complet, et non une navigation du routeur : le préfixe
        // est le `basename`
        // du routeur, une configuration et non un état réactif. Le geste est
        // rare, et un chargement neuf garantit d'un coup `<html lang>`, le bon
        // catalogue et le bon `canonical`. La cible vient du chemin du routeur
        // — jamais d'une chaîne de query string (T-96-08).
        window.location.assign(withLang(pathname, code))
      }}
      className={`text-sm font-medium uppercase tracking-[0.1em] transition-colors duration-200 cursor-pointer rounded-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${
        active ? 'text-accent' : 'text-text-muted hover:text-accent'
      }`}
    >
      {short}
    </button>
  )
}

export default function LanguageToggle() {
  return (
    <div className="flex items-center gap-2">
      <LangButton code="fr" short="FR" label="lang.fr" />
      <span aria-hidden="true" className="text-border text-sm select-none">
        /
      </span>
      <LangButton code="en" short="EN" label="lang.en" />
    </div>
  )
}
